import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import ShareFab from "./components/ShareFab";
import { MessageCircle } from "lucide-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CallProvider } from "./context/CallContext";
import { CallOverlay, IncomingCallToast } from "./components/CallUI";
import Navbar from "./components/Navbar";
import { VelocityAIWidget } from "./components/ai/VelocityAIWidget";
import VelocityAI from "./pages/VelocityAI";
import SalesDesk from "./pages/SalesDesk";
import AnimaticShare from "./pages/media/AnimaticShare";
import GigSeries from "./pages/GigSeries";
import SeriesPage from "./pages/SeriesPage";
import Careers from "./pages/Careers";
import Talent from "./pages/Talent";
import InterviewPick from "./pages/InterviewPick";
import ApplicationStatus from "./pages/ApplicationStatus";
import SecurityDashboard from "./pages/SecurityDashboard";
import Landing from "./pages/Landing";
import Gate from "./pages/Gate";
import WelcomeTour from "./components/WelcomeTour";
import TvMode from "./pages/TvMode";
import TvReceiver from "./pages/TvReceiver";
import Affiliates from "./pages/Affiliates";
import CreditGiftClaim from "./pages/CreditGiftClaim";
import OfferAction from "./pages/OfferAction";
import TvDealer from "./pages/TvDealer";
import Browse from "./pages/Browse";
import CarDetail from "./pages/CarDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ConfirmLogin from "./pages/ConfirmLogin";
import LenderPortal from "./pages/LenderPortal";
import InvestorReply from "./pages/InvestorReply";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import ListCar from "./pages/ListCar";
import GigHub from "./pages/GigHub";
import DeliveryJobs from "./pages/DeliveryJobs";
import TrackDelivery from "./pages/TrackDelivery";
import Membership from "./pages/Membership";
import FanClub from "./pages/FanClub";
import GigLifeTickets, { GigLifeScan } from "./pages/GigLifeTickets";
import Learn from "./pages/Learn";
import VerifyCertificate from "./pages/VerifyCertificate";
import PartnerPortal from "./pages/PartnerPortal";
import PartnerApply from "./pages/PartnerApply";
import Faq from "./pages/Faq";
import Manual from "./pages/Manual";
import AutoCare from "./pages/AutoCare";
import AutoCareRequests from "./pages/AutoCareRequests";
import AutoCareQuote from "./pages/AutoCareQuote";
import Academy from "./pages/Academy";
import CommunityLayout from "./components/CommunityLayout";
import CommunityFeed from "./pages/community/CommunityFeed";
import CommunityGroups from "./pages/community/CommunityGroups";
import CommunityGroup from "./pages/community/CommunityGroup";
import CommunityMessages from "./pages/community/CommunityMessages";
import CommunityEvents from "./pages/community/CommunityEvents";
import CommunityMembers from "./pages/community/CommunityMembers";
import CommunitySearch from "./pages/community/CommunitySearch";
import CommunityProfile from "./pages/community/CommunityProfile";
import CommunityLive from "./pages/community/CommunityLive";
import Investors from "./pages/Investors";
import Transport from "./pages/Transport";
import TrackLoad from "./pages/TrackLoad";
import TransportQuote from "./pages/TransportQuote";
import TransportCheckin from "./pages/TransportCheckin";
import CarrierPublic from "./pages/CarrierPublic";
import Franchise from "./pages/Franchise";
import OperatorPortal from "./pages/OperatorPortal";
import Dealers from "./pages/Dealers";
import DealerPublic from "./pages/DealerPublic";
import DealerScan from "./pages/DealerScan";
import ShowroomLeaderboard from "./pages/ShowroomLeaderboard";
import Distribution from "./pages/Distribution";
import DesignStudio from "./pages/DesignStudio";
import Claims from "./pages/Claims";
import Insurance from "./pages/Insurance";
import TvNetwork from "./pages/TvNetwork";
import TvShow, { TvUnsub } from "./pages/TvShow";
import TvPremiere from "./pages/TvPremiere";
import CreatorPortal from "./pages/CreatorPortal";
import MediaLayout from "./components/MediaLayout";
import Studio from "./pages/Studio";
import GigLife from "./pages/GigLife";
import VelocityFilms, { FilmPage } from "./pages/VelocityFilms";
import Premiere from "./pages/Premiere";
import Rei from "./pages/rei/Rei";
import ReiProperties, { ReiPropertyDetail } from "./pages/rei/ReiProperties";
import ReiSubmit from "./pages/rei/ReiSubmit";
import ReiPartners from "./pages/rei/ReiPartners";
import ReiAcademy from "./pages/rei/ReiAcademy";
import ReiSponsorPay from "./pages/rei/ReiSponsorPay";
import ReiDeals from "./pages/rei/ReiDeals";
import ReiTaxDeeds from "./pages/rei/ReiTaxDeeds";
import ReiTaxDeedCertificate from "./pages/rei/ReiTaxDeedCertificate";
import Vlo from "./pages/Vlo";
import GigLifePartners from "./pages/GigLifePartners";
import AgentPortal from "./pages/AgentPortal";
import Limo from "./pages/Limo";
import LimoChauffeur from "./pages/LimoChauffeur";
import LimoAdmin from "./pages/LimoAdmin";
import Freelance from "./pages/Freelance";
import FreelanceGig from "./pages/FreelanceGig";
import FreelanceOrder from "./pages/FreelanceOrder";
import { FreelanceSell, FreelanceOrders, FreelanceRequests, FreelanceSeller, FreelanceAdmin } from "./pages/FreelancePages";
import Credit from "./pages/Credit";
import Publish from "./pages/Publish";
import StudioStore from "./pages/StudioStore";
import CreatorProfile from "./pages/CreatorProfile";
import CreditAdmin from "./pages/CreditAdmin";
import Rides from "./pages/Rides";
import Drive, { TripShare, Dispatch } from "./pages/Drive";
import VelocityRadio from "./pages/VelocityRadio";
import MusicStore from "./pages/MusicStore";
import MusicStudio from "./pages/MusicStudio";
import WatchParty from "./pages/WatchParty";
import MediaOverview from "./pages/media/MediaOverview";
import MediaProject from "./pages/media/MediaProject";
import AiVideoMaker from "./pages/media/AiVideoMaker";
import MediaPlugins from "./pages/media/MediaPlugins";
import ScriptWriter from "./pages/media/ScriptWriter";
import { MediaProjects, VideoStudio, AudioStudio, ProductionTeam, BrandLibrary, MediaExports } from "./pages/media/MediaPages";
import EtrLayout from "./components/EtrLayout";
import EtrOverview, { EtrHistory } from "./pages/etr/EtrOverview";
import EtrRequest, { EtrNew, EtrVehicles, EtrCustomers, EtrDocuments, EtrReports, EtrSettings } from "./pages/etr/EtrRequest";
import EtrSign from "./pages/etr/EtrSign";
import EtrCompliance from "./pages/etr/EtrCompliance";
import BuyerTrack from "./pages/etr/BuyerTrack";
import DealerPortal from "./pages/DealerPortal";
import DriversLanding from "./pages/DriversLanding";
import HostsLanding from "./pages/HostsLanding";
import GrowthEnginePage from "./pages/GrowthEnginePage";
import Shop from "./pages/Shop";
import BookPreview from "./pages/BookPreview";
import GiftClaim from "./pages/GiftClaim";
import CertificatePage from "./pages/CertificatePage";
import ShareAnalytics from "./pages/ShareAnalytics";
import Orders from "./pages/Orders";
import RaqTips from "./components/RaqTips";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AdminQueue from "./pages/AdminQueue";
import BuyCars from "./pages/BuyCars";
import SaleDetail from "./pages/SaleDetail";
import SellCar from "./pages/SellCar";
import Footer from "./components/Footer";
import InstallPrompt from "./components/InstallPrompt";
import "./App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const OPEN_PATHS = [/^\/premiere$/, /^\/vlo$/, /^\/rei(\/.*)?$/, /^\/careers$/, /^\/talent$/, /^\/careers\/(interview|status)\//, /^\/chat$/, /^\/animatic\//, /^\/series$/, /^\/login$/, /^\/forgot-password$/, /^\/reset-password$/, /^\/confirm-login$/, /^\/lender$/, /^\/investor-reply$/, /^\/track\//, /^\/transport\/track\//, /^\/transport\/checkin\//,
  /^\/auto-care\/quote\//, /^\/partner\//, /^\/verify/, /^\/certificates\//, /^\/gift\//, /^\/payment\//, /^\/faq$/, /^\/manual$/, /^\/tv$/, /^\/tv\/network(\/[\w.-]+)*$/, /^\/tv-receiver$/, /^\/affiliates$/, /^\/showrooms\/leaderboard$/, /^\/gig-life$/, /^\/gig-life\/partners$/, /^\/gig-life\/(tickets|scan)\//, /^\/films(\/[\w-]+)?$/, /^\/rides$/, /^\/limo$/, /^\/credit$/, /^\/freelance(\/gigs\/|\/sellers\/|\/requests$|$)/, /^\/rides\/trip\//, /^\/radio$/, /^\/music$/, /^\/fan-club$/, /^\/studio$/, /^\/shop\/?$/, /^\/shop\/studio$/, /^\/creators\/[\w-]+$/];
const isOpen = (p) => OPEN_PATHS.some((r) => r.test(p));

function Shell() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const [concierge, setConcierge] = useState(false);
  const isChat = ["/chat", "/sales-agent"].includes(window.location.pathname);
  useEffect(() => { const ref = new URLSearchParams(location.search).get("ref"); if (ref) localStorage.setItem("velocity_ref", ref.toUpperCase().slice(0, 16)); }, [location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (location.pathname === "/tv") return <TvMode />;
  if (location.pathname === "/tv-receiver") return <TvReceiver />;
  if (location.pathname.startsWith("/tv/dealer/")) return <Routes><Route path="/tv/dealer/:dealerId" element={<TvDealer />} /></Routes>;
  if (/^\/dealers\/[^/]+\/scan/.test(location.pathname)) return <Routes><Route path="/dealers/:dealerId/scan" element={<DealerScan />} /></Routes>;
  if (location.pathname.startsWith("/etr/sign/")) return <Routes><Route path="/etr/sign/:token" element={<EtrSign />} /></Routes>;
  if (location.pathname.startsWith("/reg/")) return <Routes><Route path="/reg/:token" element={<BuyerTrack />} /></Routes>;
  if (location.pathname.startsWith("/gift/claim/")) return <Routes><Route path="/gift/claim/:token" element={<CreditGiftClaim />} /></Routes>;
  if (location.pathname === "/offer-action") return <OfferAction />;
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!user && location.pathname === "/") return <Gate />;
  if (!user && !isOpen(location.pathname)) return <Navigate to="/" replace />;
  return (
    <>
      <Navbar onOpenConcierge={() => setConcierge(true)} />
      <Routes>
        <Route path="/" element={<Landing onOpenConcierge={() => setConcierge(true)} />} />
        <Route path="/affiliates" element={<Affiliates />} />
        <Route path="/distribution" element={<Protected><Distribution /></Protected>} />
        <Route path="/design" element={<Protected><DesignStudio /></Protected>} />
        <Route path="/claims" element={<Protected><Claims /></Protected>} />
        <Route path="/insurance" element={<Protected><Insurance /></Protected>} />
        <Route path="/tv/network" element={<TvNetwork />} />
        <Route path="/tv/network/unsubscribe/:token" element={<TvUnsub />} />
        <Route path="/tv/network/live/:premiereId" element={<TvPremiere />} />
        <Route path="/tv/creator" element={<Protected><CreatorPortal /></Protected>} />
        <Route path="/tv/network/:showId" element={<TvShow />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/cars/:carId" element={<CarDetail />} />
        <Route path="/gig-hub" element={<GigHub />} />
        <Route path="/delivery" element={<DeliveryJobs />} />
        <Route path="/track/:token" element={<TrackDelivery />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/confirm-login" element={<ConfirmLogin />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/list-your-car" element={<Protected><ListCar /></Protected>} />
        <Route path="/membership" element={<Protected><Membership /></Protected>} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/verify" element={<VerifyCertificate />} />
        <Route path="/verify/:certId" element={<VerifyCertificate />} />
        <Route path="/partner/:token" element={<PartnerPortal />} />
        <Route path="/partners/apply" element={<PartnerApply />} />
        <Route path="/faq" element={<Faq onOpenConcierge={() => setConcierge(true)} />} />
        <Route path="/manual" element={<Manual />} />
        <Route path="/auto-care" element={<AutoCare />} />
        <Route path="/auto-care/requests" element={<AutoCareRequests />} />
        <Route path="/auto-care/quote/:token" element={<AutoCareQuote />} />
        <Route path="/university" element={<Academy />} />
        <Route path="/academy" element={<Academy />} />
        <Route path="/community" element={<Protected><CommunityLayout /></Protected>}>
          <Route index element={<CommunityFeed />} />
          <Route path="groups" element={<CommunityGroups />} />
          <Route path="g/:slug" element={<CommunityGroup />} />
          <Route path="messages" element={<CommunityMessages />} />
          <Route path="events" element={<CommunityEvents />} />
          <Route path="members" element={<CommunityMembers />} />
          <Route path="live" element={<CommunityLive />} />
          <Route path="search" element={<CommunitySearch />} />
          <Route path="u/:userId" element={<CommunityProfile />} />
        </Route>
        <Route path="/investors" element={<Investors />} />
        <Route path="/lender" element={<LenderPortal />} />
        <Route path="/investor-reply" element={<InvestorReply />} />
        <Route path="/transport" element={<Transport />} />
        <Route path="/transport/track/:token" element={<TrackLoad />} />
        <Route path="/transport/quote" element={<TransportQuote />} />
        <Route path="/transport/checkin/:loadId" element={<TransportCheckin />} />
        <Route path="/carrier/:userId" element={<CarrierPublic />} />
        <Route path="/franchise" element={<Franchise />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/talent" element={<Talent />} />
        <Route path="/careers/interview/:token" element={<InterviewPick />} />
        <Route path="/careers/status/:token" element={<ApplicationStatus />} />
        <Route path="/operator" element={<OperatorPortal />} />
        <Route path="/dealers" element={<Dealers />} />
        <Route path="/showrooms/leaderboard" element={<ShowroomLeaderboard />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/gig-life" element={<GigLife />} />
        <Route path="/premiere" element={<Premiere />} />
        <Route path="/rei" element={<Rei />} />
        <Route path="/rei/properties" element={<ReiProperties />} />
        <Route path="/rei/properties/:id" element={<ReiPropertyDetail />} />
        <Route path="/rei/submit" element={<ReiSubmit />} />
        <Route path="/rei/partners" element={<ReiPartners />} />
        <Route path="/rei/academy" element={<ReiAcademy />} />
        <Route path="/rei/sponsor/pay/:id" element={<ReiSponsorPay />} />
        <Route path="/rei/deals" element={<ReiDeals />} />
        <Route path="/rei/tax-deeds" element={<ReiTaxDeeds />} />
        <Route path="/rei/tax-deeds/certificate/:certId" element={<ReiTaxDeedCertificate />} />
        <Route path="/vlo" element={<Vlo />} />
        <Route path="/gig-life/partners" element={<GigLifePartners />} />
        <Route path="/gig-life/partners/portal" element={<Protected><AgentPortal /></Protected>} />
        <Route path="/gig-life/tickets/:orderId" element={<GigLifeTickets />} />
        <Route path="/gig-life/scan/:code" element={<GigLifeScan />} />
        <Route path="/films" element={<VelocityFilms />} />
        <Route path="/series" element={<SeriesPage />} />
        <Route path="/films/:slug" element={<FilmPage />} />
        <Route path="/films/party/:pid" element={<Protected><WatchParty /></Protected>} />
        <Route path="/rides" element={<Rides />} />
        <Route path="/rides/drive" element={<Protected><Drive /></Protected>} />
        <Route path="/rides/dispatch" element={<Protected><Dispatch /></Protected>} />
        <Route path="/rides/trip/:token" element={<TripShare />} />
        <Route path="/animatic/:token" element={<AnimaticShare />} />
        <Route path="/limo" element={<Limo />} />
        <Route path="/freelance" element={<Freelance />} />
        <Route path="/freelance/gigs/:gid" element={<FreelanceGig />} />
        <Route path="/freelance/sellers/:uid" element={<FreelanceSeller />} />
        <Route path="/freelance/requests" element={<FreelanceRequests />} />
        <Route path="/freelance/sell" element={<Protected><FreelanceSell /></Protected>} />
        <Route path="/freelance/orders" element={<Protected><FreelanceOrders /></Protected>} />
        <Route path="/freelance/orders/:oid" element={<Protected><FreelanceOrder /></Protected>} />
        <Route path="/freelance/admin" element={<Protected><FreelanceAdmin /></Protected>} />
        <Route path="/chat" element={<VelocityAI />} />
        <Route path="/sales-agent" element={<Protected><SalesDesk /></Protected>} />
        <Route path="/credit" element={<Credit />} />
        <Route path="/credit/admin" element={<Protected><CreditAdmin /></Protected>} />
        <Route path="/gig-series" element={<Protected><GigSeries /></Protected>} />
        <Route path="/admin/security" element={<Protected><SecurityDashboard /></Protected>} />
        <Route path="/publish" element={<Protected><Publish /></Protected>} />
        <Route path="/limo/chauffeur" element={<Protected><LimoChauffeur /></Protected>} />
        <Route path="/limo/admin" element={<Protected><LimoAdmin /></Protected>} />
        <Route path="/radio" element={<VelocityRadio />} />
        <Route path="/music" element={<MusicStore />} />
        <Route path="/music/studio" element={<Protected><MusicStudio /></Protected>} />
        <Route path="/fan-club" element={<FanClub />} />
        <Route path="/media" element={<Protected><MediaLayout /></Protected>}>
          <Route index element={<MediaOverview />} />
          <Route path="ai" element={<AiVideoMaker />} />
          <Route path="plugins" element={<MediaPlugins />} />
          <Route path="scripts" element={<ScriptWriter />} />
          <Route path="video" element={<VideoStudio />} />
          <Route path="audio" element={<AudioStudio />} />
          <Route path="team" element={<ProductionTeam />} />
          <Route path="brand" element={<BrandLibrary />} />
          <Route path="projects" element={<MediaProjects />} />
          <Route path="projects/:pid" element={<MediaProject />} />
          <Route path="exports" element={<MediaExports />} />
        </Route>
        <Route path="/etr" element={<Protected><EtrLayout /></Protected>}>
          <Route index element={<EtrOverview />} />
          <Route path="new" element={<EtrNew />} />
          <Route path="requests/:rid" element={<EtrRequest />} />
          <Route path="vehicles" element={<EtrVehicles />} />
          <Route path="customers" element={<EtrCustomers />} />
          <Route path="documents" element={<EtrDocuments />} />
          <Route path="history" element={<EtrHistory />} />
          <Route path="compliance" element={<EtrCompliance />} />
          <Route path="reports" element={<EtrReports />} />
          <Route path="settings" element={<EtrSettings />} />
        </Route>
        <Route path="/dealers/:dealerId" element={<DealerPublic />} />
        <Route path="/dealer" element={<DealerPortal />} />
        <Route path="/drivers" element={<DriversLanding />} />
        <Route path="/hosts" element={<HostsLanding />} />
        <Route path="/growth" element={<GrowthEnginePage />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/studio" element={<StudioStore />} />
        <Route path="/creators/:uid" element={<CreatorProfile />} />
        <Route path="/shop/books/:slug" element={<BookPreview />} />
        <Route path="/gift/:code" element={<GiftClaim />} />
        <Route path="/certificates/:certId" element={<CertificatePage />} />
        <Route path="/share-stats" element={<Protected><ShareAnalytics /></Protected>} />
        <Route path="/orders" element={<Protected><Orders /></Protected>} />
        <Route path="/admin" element={<Protected><AdminQueue /></Protected>} />
        <Route path="/buy" element={<BuyCars />} />
        <Route path="/buy/:listingId" element={<SaleDetail />} />
        <Route path="/sell" element={<Protected><SellCar /></Protected>} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
      {!isChat && <Footer />}
      {!isChat && !concierge && <ShareFab />}
      {!concierge && !isChat && (
        <button onClick={() => setConcierge(true)} data-testid="support-chat-fab" aria-label="Open support chat"
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 text-slate-950 shadow-lg shadow-cyan-500/30 flex items-center justify-center hover:scale-105 transition-transform">
          <MessageCircle className="w-6 h-6" />
          <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0B0D12]" />
        </button>
      )}
      <VelocityAIWidget open={concierge} onClose={() => setConcierge(false)} />
      <RaqTips />
      <InstallPrompt />
      <WelcomeTour />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CallProvider>
            <Shell />
            <IncomingCallToast />
            <CallOverlay />
            <Toaster theme="dark" position="top-right" />
          </CallProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
